import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SANDBOX_MODE_ENABLED } from "@/config/productionMode";


const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: "€",
  USD: "$",
  GBP: "£",
  CHF: "CHF",
  JPY: "¥",
  CAD: "C$",
  AUD: "A$",
};

export function usePlatformSettings() {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["platform_settings"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("get_public_platform_settings");
      if (error) throw error;
      return (data ?? null) as any;
    },
    // Kept fresh by realtime + polling fallback in useConfigRealtime.
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });

  const baseCurrency = (data?.base_currency as string) || "USD";
  const secondaryCurrencies = (data?.secondary_currencies as string[]) || ["USD"];
  const currencySymbol = CURRENCY_SYMBOLS[baseCurrency] || baseCurrency;
  const devMode = (data?.dev_mode as boolean) ?? false;
  // Sandbox is disabled in production: always resolve to live mode.
  const environmentMode = SANDBOX_MODE_ENABLED ? ((data?.environment_mode as string) || "sandbox") : "live";
  const isSandbox = SANDBOX_MODE_ENABLED && environmentMode === "sandbox";


  // ── Crypto withdrawal auto-approve controls ──
  const cryptoWithdrawalAutoApprove = (data?.crypto_withdrawal_auto_approve as boolean) ?? false;
  const cryptoWithdrawalAutoApproveMaxUsd = Number(data?.crypto_withdrawal_auto_approve_max_usd ?? 500);
  const cryptoWithdrawalAutoApproveMaxRisk = Number(data?.crypto_withdrawal_auto_approve_max_risk ?? 60);
  const cryptoWithdrawalAutoApproveMinTier = Number(data?.crypto_withdrawal_auto_approve_min_tier ?? 2);
  const cryptoWithdrawalRequireWhitelist = (data?.crypto_withdrawal_require_whitelist as boolean) ?? false;

  return {
    baseCurrency,
    secondaryCurrencies,
    currencySymbol,
    devMode,
    environmentMode,
    isSandbox,
    cryptoWithdrawalAutoApprove,
    cryptoWithdrawalAutoApproveMaxUsd,
    cryptoWithdrawalAutoApproveMaxRisk,
    cryptoWithdrawalAutoApproveMinTier,
    cryptoWithdrawalRequireWhitelist,
    loading: isLoading,
    refreshing: isFetching,
    settingsId: data?.id as string | undefined,
  };
}

export { CURRENCY_SYMBOLS };
